import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BadgeCheck, Plus } from "lucide-react";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import Loading from "../common/Loading";
import { toast } from "react-toastify";
import { getAdAccounts } from "../../hook/useAdsApi";
import { getAdvertiserTypeIcon } from "./adsConstants";

export default function AdAccountTypeSelect() {
  const navigate = useNavigate();
  const location = useLocation();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    getAdAccounts()
      .then((list) => {
        setAccounts(list);
        if (list.length > 0) setSelectedId(list[0].id);
      })
      .catch(() => toast.error("Failed to load ad accounts", { toastId: "ad-accounts-load-error" }))
      .finally(() => setLoading(false));
  }, []);

  const handleContinue = () => {
    const account = accounts.find((a) => a.id === selectedId);
    if (!account) {
      toast.error("Select an ad account to continue");
      return;
    }
    navigate(location.state?.next || "/ads/create", { state: { adAccount: account } });
  };

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <button onClick={() => navigate(-1)} className="rounded-full bg-[#e7f3ea] p-2">
                ←
              </button>
            </div>

            {/* Exact type/colors from Figma's "Select ad Account Type" screen (read-only access). */}
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-[22px] font-semibold text-black capitalize">Select Ad Account Type</h1>
                <p className="text-sm text-[#747474] mt-2">Choose the account you want to advertise with :</p>
              </div>
              <button
                type="button"
                onClick={() => navigate("/ads/add-account", { state: { next: location.state?.next } })}
                className="flex items-center gap-2 bg-[#095228] border border-[#39b54a] text-[#f9e9ea] font-medium text-lg px-6 py-3 rounded-full shadow-[0_0_6px_rgba(59,182,76,0.53)]"
              >
                <Plus size={20} />
                Add /Request Account
              </button>
            </div>
            <div className="h-1 bg-[#d9d9d9] rounded-full my-4" />

            {loading ? (
              <div className="flex justify-center mt-16">
                <Loading />
              </div>
            ) : accounts.length === 0 ? (
              <p className="text-gray-500 text-sm mb-6">You don't have any ad accounts yet.</p>
            ) : (
              <div className="space-y-4 mb-8">
                {accounts.map((account) => {
                  const iconSrc = getAdvertiserTypeIcon(account.advertiserType?.code);
                  const selected = selectedId === account.id;
                  return (
                    <label
                      key={account.id}
                      className={`flex items-center justify-between gap-4 bg-white border border-[#98911f] rounded-[20px] px-5 py-4 cursor-pointer ${
                        selected ? "shadow-[0_0_0_5px_rgba(59,182,76,0.2)]" : ""
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        {iconSrc && <img src={iconSrc} alt="" className="w-8 h-8 shrink-0" />}
                        <div>
                          <p className="text-base font-semibold text-black">
                            {account.advertiserType?.name || account.advertiser_type_id}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            <BadgeCheck size={14} className="text-[#095228]" />
                            <p className="text-[10px] font-medium text-[#095228]">
                              {account.status === "Active" ? "Verified" : account.status}
                            </p>
                          </div>
                        </div>
                      </div>
                      <input
                        type="radio"
                        name="adAccount"
                        checked={selected}
                        onChange={() => setSelectedId(account.id)}
                        className="sr-only"
                      />
                      <span
                        className={`shrink-0 w-6 h-6 rounded-full bg-[#fefdeb] ${
                          selected ? "border-[6px] border-[#095228]" : "border-2 border-[#707070]"
                        }`}
                      />
                    </label>
                  );
                })}
              </div>
            )}

            {accounts.length > 0 && (
              <button
                type="button"
                onClick={handleContinue}
                className="bg-[#095228] border border-[#39b54a] text-[#f9e9ea] font-medium text-lg px-10 py-3 rounded-full shadow-[0_0_6px_rgba(59,182,76,0.53)]"
              >
                Next
              </button>
            )}
          </div>
        </main>
      </main>
    </div>
  );
}
